import React, { useEffect, useRef, useState } from "react";
import { getLenderProjectOverview } from "services/projectService";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import MailIcon from "Assets/Img/MailIcon";
import PhoneIcon from "Assets/Img/PhoneIcon";

interface Stakeholder {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyName?: string;
  address?: string;
}

interface Inspector extends Stakeholder {
  role?: string;
}

interface Stakeholders {
  owner?: Stakeholder;
  gc?: Stakeholder;
  lender?: Stakeholder;
  inspectors?: Inspector[];
  architect?: Stakeholder;
  admin?: Stakeholder;
}

interface ContactCardProps {
  role: string;
  name: string;
  company?: string;
  phone?: string;
  email?: string;
}

function ContactCard({ role, name, company, phone, email }: ContactCardProps) {
  const initials = name
    .split(" ")
    .map((w) => w[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="contact-card">
      <div className="contact-av">{initials}</div>
      <div className="contact-info">
        <div className="contact-role">{role}</div>
        <div className="contact-name">{name}</div>
        {company && <div className="contact-detail">{company}</div>}
        {phone && (
          <div className="contact-detail">
            <PhoneIcon /> {phone}
          </div>
        )}
        {email && (
          <div className="contact-detail">
            <span style={{ color: "inherit" }}>
              <MailIcon style={{ verticalAlign: "middle" }} /> {email}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

const fullName = (p?: Stakeholder) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(" ") || "—";

export default function ContactsSection({ projectId }: { projectId?: string }) {
  const [stakeholders, setStakeholders] = useState<Stakeholders | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!projectId || fetchedRef.current) return;
    fetchedRef.current = true;
    (getLenderProjectOverview(projectId) as Promise<any>).then((res: any) => {
      if (!res.error && res.data?.data?.stakeholders) {
        setStakeholders(res.data.data.stakeholders);
      }
      setLoading(false);
    });
  }, [projectId]);

  const buildContacts = () => {
    if (!stakeholders) return [];
    const list: ContactCardProps[] = [];

    if (stakeholders?.inspectors?.length) {
      stakeholders?.inspectors.forEach((i) =>
        list.push({
          role: `${i?.companyName} — Inspector`,
          name: fullName(i),
          phone: i.phone,
          email: i.email,
        }),
      );
    }
    if (stakeholders?.admin) {
      list.push({
        role: `${stakeholders?.admin.companyName} — Admin`,
        name: fullName(stakeholders?.admin),
        company: stakeholders?.admin.companyName,
        phone: stakeholders?.admin.phone,
        email: stakeholders?.admin.email,
      });
    }
    if (stakeholders?.owner) {
      list.push({
        role: "Owner / Borrower",
        name: stakeholders?.owner.companyName || "",
        company: fullName(stakeholders?.owner),
        phone: stakeholders?.owner.phone || "",
        email: stakeholders?.owner.email || "",
      });
    }
    if (stakeholders?.gc) {
      list.push({
        role: "General Contractor",
        name: stakeholders?.gc.companyName || "",
        company: fullName(stakeholders?.gc),
        phone: stakeholders?.gc.phone,
        email: stakeholders?.gc.email,
      });
    }
    if (stakeholders?.architect) {
      list.push({
        role: "Architect",
        name: stakeholders?.architect.companyName || "",
        company: fullName(stakeholders?.architect),
        phone: stakeholders?.architect.phone,
        email: stakeholders?.architect.email,
      });
    }

    return list;
  };

  return (
    <div className="ds active">
      <div className="dsHeader">
        <div className="dsHeaderTitle">Project Contacts</div>
      </div>

      {loading ? (
        <div className="contact-grid">
          {[1, 2, 3, 4].map((n) => (
            <div className="contact-card" key={n}>
              <Skeleton width={44} height={44} borderRadius={22} />
              <div style={{ flex: 1 }}>
                <Skeleton width="40%" height={11} style={{ marginBottom: 6 }} />
                <Skeleton width="70%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="55%" height={11} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="contact-grid">
          {buildContacts().map((c) => (
            <ContactCard key={`${c.role}-${c.name}`} {...c} />
          ))}
        </div>
      )}
    </div>
  );
}
